"use client";

import { useEffect, useState } from "react";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";

import MemoryFlowchart from "@/components/ui/memory-flowchart";
import PageTitle from "@/components/ui/page-title";
import StudyHarmonizationFlowchart from "@/components/ui/study-harmonization-flowchart";

type Tab = "Estimation of cognitive functioning in the HCAP studies" | "Imputation";

export default function Page() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const tabFromQuery = searchParams.get("tab") as Tab | null;

  const [activeTab, setActiveTab] = useState<Tab>(
    tabFromQuery ?? "Estimation of cognitive functioning in the HCAP studies",
  );

  // Update tab if query changes
  useEffect(() => {
    if (tabFromQuery) setActiveTab(tabFromQuery);
  }, [tabFromQuery]);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);

    // Update the URL query param without a full page reload
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tab);
    router.push(url.pathname + url.search);
  };

  const renderComponent = () => {
    if (activeTab === "Estimation of cognitive functioning in the HCAP studies") {
      return (
        <div className="space-y-4 mt-4">
          <p>
            Many HCAP studies include derived estimates of general cognitive functioning and specific domains of
            cognitive functioning (memory, executive functioning, language/fluency, orientation, visuospatial
            functioning), based on confirmatory factor analysis models.
          </p>

          <MemoryFlowchart />

          <p>
            Models use state-of-the-art advanced psychometric techniques to model the theoretical structure underlying
            cognition and account for measurement error and inter-correlations between items from the same task (e.g.,
            immediate and delayed word recall).
          </p>

          <p>
            Studies to assess the structure and measurement of cognition using these methods have been conducted for:
          </p>

          <ul>
            <li>
              <a
                className="g2-text-blue"
                href="https://www.cambridge.org/core/journals/journal-of-the-international-neuropsychological-society/article/abs/factor-structure-of-the-harmonized-cognitive-assessment-protocol-neuropsychological-battery-in-the-health-and-retirement-study/8910F8A75C953C1F42747AEC06659E0C"
                target="_blank"
              >
                HRS-HCAP{" "}
              </a>
            </li>
            <li>
              <a className="g2-text-blue" href="https://doi.org/10.1016/j.inpsyc.2025.100042" target="_blank">
                ELSA-HCAP{" "}
              </a>
            </li>
            <li>
              <a
                className="g2-text-blue"
                href="https://agsjournals.onlinelibrary.wiley.com/doi/full/10.1111/jgs.16738"
                target="_blank"
              >
                LASI-DAD{" "}
              </a>
            </li>
          </ul>

          <p>
            The harmonization of cognitive functioning across studies requires more detailed, careful examination of
            differences between studies in the administration and scoring of cognitive test items. Once harmonized items
            are identified, psychometric methods rooted in item response theory can enable statistical harmonization
            leveraging information from both harmonized and unharmonized items to obtain estimates of cognitive function
            that are both harmonized and which have maximal precision. General steps in the process are shown below.
          </p>

          <StudyHarmonizationFlowchart />

          <span className="mt-4 g2-text-grey">Harmonized estimates of cognition are currently available for:</span>
          <ul>
            <li>LASI-DAD</li>
            <li>Mex-Cog</li>
            <li>ELSA-HCAP</li>
            <li>HRS-HCAP</li>
          </ul>

          <span className="mt-4 g2-text-grey">
            Additional details on this process can be found in the following papers:
          </span>
          <ul>
            <li>
              <a className="g2-text-blue" href="https://pubmed.ncbi.nlm.nih.gov/35482625/" target="_blank">
                Pre-statistical harmonization
              </a>
            </li>
            <li>
              <a
                className="g2-text-blue"
                href="https://www.thelancet.com/journals/lanhl/article/PIIS2666-7568(23)00170-8/fulltext"
                target="_blank"
              >
                Statistical harmonization
              </a>
            </li>
          </ul>
        </div>
      );
    }

    if (activeTab === "Imputation") {
      return (
        <div className="space-y-4 mt-4">
          <p>
            Missing data can be a big problem in survey data; missingness is particularly important in studies of
            cognitive aging, where there may be higher levels of missing data due to participants being unable or
            unwilling to complete testing. Although the magnitude of missing data is often lower than other indicators,
            such as economic variables on income and wealth, this non-random pattern of missing data can lead to bias.
            Imputation methods create datasets that are easy to analyze for researchers and that result in estimators
            that properly reflect the relations in the data. Imputation does this by modeling a variable that has
            missingness as a function of other variables in the data, then replacing missing data with draws from the
            model. See, for example, Donders, ART, van der Heijden, GJMG, Stijnen, T, & Moons, KGM (2006). *Review: A
            gentle introduction to imputation of missing values.* *Journal of Clinical Epidemiology*, 59, 1087–1091.
          </p>

          <p>
            In imputations for harmonized HCAP data we use information on a variety of topics, which may come from
            either the core HRS family study or the HCAP study:
          </p>

          <ul>
            <li>Demographics</li>
            <li>Socio-economic indicators</li>
            <li>
              General health status
              <ul>
                <li>Self-reported health</li>
                <li>Activities of daily living and instrumental activities of daily living</li>
                <li>Chronic conditions</li>
                <li>Sensory health</li>
              </ul>
            </li>
            <li>Prior cognition (from core HRS family study or earlier wave)</li>
            <li>Other items from cognitive assessments and informant report</li>
          </ul>

          <p>
            General methods: On cognitive test items, responses of “don’t know” are typically recoded as incorrect (0),
            whereas refusals are imputed. For informant reports, both are imputed. Because information on the
            abovementioned covariates was sometimes missing, imputations were conducted in a sequence of blocks of
            variables. Generally, these blocks were constructed as follows:
          </p>

          <ol className="list-decimal list-inside">
            <li>All core HRS family data</li>
            <li>Demographic and socio-economic variables from the HCAP study</li>
            <li>Non-cognitive health variables from the HCAP study</li>
            <li>Cognitive health variables from the HCAP study (objective cognitive testing and informant reports)</li>
          </ol>

          <p>
            Within each block, we used chained imputation (also known as fully conditional specification) to impute
            missing data. This involves cycling over the set of variables, using the latest updated imputations of the
            other variables in the block as covariates. Imputations were pseudo-random draws from regression models
            predicting each variable with missing data based on all observed information in previously imputed blocks
            and the current block. More specific methodological details on this process are available in the codebooks
            for the harmonized HCAP data available{" "}
            <Link target="_blank" href={"/harmonized-hcap-data/overview"} className="g2-link-blue text-decoration-none">
              here
            </Link>
            .
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <PageTitle title="Methodological Information" />
      <div>
        <ul className="nav nav-pills d-flex justify-content-evenly mb-3">
          {["Estimation of cognitive functioning in the HCAP studies", "Imputation"].map((tab) => (
            <li className="nav-item" key={tab}>
              <a
                href="#"
                className={`nav-link ${activeTab === tab ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleTabChange(tab as Tab);
                }}
              >
                {tab}
              </a>
            </li>
          ))}
        </ul>

        {renderComponent()}
      </div>
    </>
  );
}
